import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { SampleRequestItem } from "@/api/sample-requests";

export type SelectedSample = {
  applicationId: string;
  collaborationId: string | null;
  name: string;
};

/** Mirrors the name resolution used by the single-row RowActions. */
export const toSelectedSample = (item: SampleRequestItem): SelectedSample => ({
  applicationId: item.applicationId,
  collaborationId: item.collaborationId,
  name: item.creator?.nickname ?? item.creator?.username ?? item.sku?.name ?? "creator",
});

type SampleSelectionCtx = {
  selected: Map<string, SelectedSample>;
  count: number;
  isSelected: (id: string) => boolean;
  toggle: (item: SelectedSample) => void;
  toggleMany: (items: SelectedSample[], checked: boolean) => void;
  clear: () => void;
};

const Ctx = createContext<SampleSelectionCtx | null>(null);

export function SampleSelectionProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<Map<string, SelectedSample>>(new Map());

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  const toggle = useCallback((item: SelectedSample) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.applicationId)) next.delete(item.applicationId);
      else next.set(item.applicationId, item);
      return next;
    });
  }, []);

  const toggleMany = useCallback((items: SelectedSample[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        if (checked) next.set(item.applicationId, item);
        else next.delete(item.applicationId);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Map()), []);

  const value = useMemo<SampleSelectionCtx>(
    () => ({ selected, count: selected.size, isSelected, toggle, toggleMany, clear }),
    [selected, isSelected, toggle, toggleMany, clear],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSampleSelection() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSampleSelection must be used within a SampleSelectionProvider");
  return ctx;
}
